import { Shield, Cookie, FileText, Lock, RotateCcw, Scale, Landmark } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface PolicyNavigationProps {
  currentPolicy: 'privacy' | 'terms' | 'cookie' | 'gdpr' | 'dpdp' | 'trust-center' | 'refund';
  onNavigate?: (view: string) => void;
}

const POLICY_IDS = ['privacy', 'terms', 'gdpr', 'dpdp', 'trust-center', 'cookie', 'refund'] as const;
const POLICY_TITLE_KEYS: Record<(typeof POLICY_IDS)[number], string> = {
  privacy: 'legal.privacyTitle',
  terms: 'legal.termsTitle',
  gdpr: 'legal.gdprTitle',
  dpdp: 'legal.dpdpTitle',
  'trust-center': 'legal.trustTitle',
  cookie: 'legal.cookieTitle',
  refund: 'legal.refundTitle',
};
const POLICY_DESC_KEYS: Record<(typeof POLICY_IDS)[number], string> = {
  privacy: 'legal.policyDescPrivacy',
  terms: 'legal.policyDescTerms',
  gdpr: 'legal.policyDescGdpr',
  dpdp: 'legal.policyDescDpdp',
  'trust-center': 'legal.policyDescTrust',
  cookie: 'legal.policyDescCookie',
  refund: 'legal.policyDescRefund',
};

const PolicyNavigation: React.FC<PolicyNavigationProps> = ({ currentPolicy, onNavigate }) => {
  const { t } = useTranslation();
  const policies = [
    { id: 'privacy' as const, titleKey: POLICY_TITLE_KEYS.privacy, descKey: POLICY_DESC_KEYS.privacy, icon: Lock, path: '/privacy-policy', view: 'privacy-policy' },
    { id: 'terms' as const, titleKey: POLICY_TITLE_KEYS.terms, descKey: POLICY_DESC_KEYS.terms, icon: FileText, path: '/terms-of-service', view: 'terms-of-service' },
    { id: 'gdpr' as const, titleKey: POLICY_TITLE_KEYS.gdpr, descKey: POLICY_DESC_KEYS.gdpr, icon: Shield, path: '/gdpr-compliance', view: 'gdpr-compliance' },
    { id: 'dpdp' as const, titleKey: POLICY_TITLE_KEYS.dpdp, descKey: POLICY_DESC_KEYS.dpdp, icon: Landmark, path: '/dpdp-compliance', view: 'dpdp-compliance' },
    { id: 'trust-center' as const, titleKey: POLICY_TITLE_KEYS['trust-center'], descKey: POLICY_DESC_KEYS['trust-center'], icon: Scale, path: '/trust-center', view: 'trust-center' },
    { id: 'cookie' as const, titleKey: POLICY_TITLE_KEYS.cookie, descKey: POLICY_DESC_KEYS.cookie, icon: Cookie, path: '/cookie-policy', view: 'cookie-policy' },
    { id: 'refund' as const, titleKey: POLICY_TITLE_KEYS.refund, descKey: POLICY_DESC_KEYS.refund, icon: RotateCcw, path: '/refund-cancellation', view: 'refund-cancellation' },
  ];

  const filteredPolicies = policies.filter(policy => policy.id !== currentPolicy);

  const handleClick = (view: string, path: string) => {
    if (onNavigate) {
      onNavigate(view);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.open(path, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="mt-12 pt-8 border-t vscode-border">
      <h3 className="text-xl font-semibold vscode-text-primary mb-6">{t('legal.relatedDocuments')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredPolicies.map((policy) => {
          const Icon = policy.icon;
          return (
            <button
              key={policy.id}
              onClick={() => handleClick(policy.view, policy.path)}
              className="group vscode-bg-secondary hover:vscode-bg-hover border vscode-border hover:border-[var(--vscode-primary)] p-4 text-left transition-all duration-200"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold vscode-text-primary mb-1 group-hover:text-[var(--vscode-primary)] transition-colors">
                    {t(policy.titleKey)}
                  </h4>
                  <p className="text-xs vscode-text-secondary">
                    {t(policy.descKey)}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PolicyNavigation;

